/**
 * Admin header badge showing frontend runtime-error count in the last 24h.
 * Click opens a dropdown with the most recent errors for quick triage.
 * Each row has a Dismiss (X) button; header has a "Clear all" action.
 */
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { AlertTriangle, ExternalLink, X, Trash2 } from 'lucide-react';

const API_URL = window.location.origin;
const POLL_INTERVAL_MS = 60_000;

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

const FrontendErrorBadge = () => {
  const [count, setCount] = useState(0);
  const [errors, setErrors] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());
  const [clearingAll, setClearingAll] = useState(false);
  const wrapperRef = useRef(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const fetchErrors = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/frontend-errors?limit=20&since_hours=24`,
        { headers: getAuthHeaders() }
      );
      setCount(res.data?.total ?? 0);
      setErrors(res.data?.errors ?? []);
    } catch (_) {
      // Silent — don't spam the admin UI if this widget fails
    }
  };

  useEffect(() => {
    fetchErrors();
    const id = setInterval(fetchErrors, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleToggle = async () => {
    setOpen((v) => !v);
    if (!open) {
      setLoading(true);
      await fetchErrors();
      setLoading(false);
    }
  };

  const handleDismiss = async (errorId) => {
    if (!errorId) return;
    setBusyIds((prev) => new Set(prev).add(errorId));
    try {
      await axios.delete(
        `${API_URL}/api/admin/frontend-errors/${encodeURIComponent(errorId)}`,
        { headers: getAuthHeaders() }
      );
      // Optimistic update
      setErrors((prev) => prev.filter((e) => e.error_id !== errorId));
      setCount((c) => Math.max(0, c - 1));
    } catch (_) {
      // keep the row; user can retry
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(errorId);
        return next;
      });
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm(
      `Dismiss all ${count} error${count === 1 ? '' : 's'} from the last 24h?\n\n` +
      `This clears them from the admin view. Records older than 24h remain.`
    )) return;
    setClearingAll(true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/frontend-errors?since_hours=24`,
        { headers: getAuthHeaders() }
      );
      setErrors([]);
      setCount(0);
      setOpen(false);
    } catch (_) {
      // noop — user can retry
    } finally {
      setClearingAll(false);
    }
  };

  if (count === 0) return null;

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={handleToggle}
        data-testid="frontend-error-badge"
        title={`${count} frontend error${count === 1 ? '' : 's'} in the last 24h`}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-medium hover:bg-red-100 transition-colors"
      >
        <AlertTriangle className="h-3.5 w-3.5" />
        <span>{count > 99 ? '99+' : count} error{count === 1 ? '' : 's'} · 24h</span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden"
          data-testid="frontend-error-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
            <div>
              <p className="text-sm font-semibold text-forest">Frontend errors · last 24h</p>
              <p className="text-xs text-gray-500">{count} total · newest first</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchErrors}
                className="text-xs text-forest hover:underline"
                data-testid="frontend-error-refresh"
              >
                Refresh
              </button>
              <button
                onClick={handleClearAll}
                disabled={clearingAll || count === 0}
                className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline disabled:opacity-50 disabled:no-underline"
                data-testid="frontend-error-clear-all"
                title="Dismiss all errors from the last 24h"
              >
                <Trash2 className="h-3 w-3" />
                {clearingAll ? 'Clearing...' : 'Clear all'}
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && errors.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">Loading...</div>
            ) : errors.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">No errors in the last 24h.</div>
            ) : (
              errors.map((e, idx) => {
                const isBusy = busyIds.has(e.error_id);
                return (
                  <div
                    key={e.id || idx}
                    className={`px-4 py-3 border-b border-gray-50 last:border-b-0 hover:bg-gray-50 group ${isBusy ? 'opacity-50' : ''}`}
                    data-testid={`frontend-error-item-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm font-medium text-gray-900 break-words flex-1">
                        {e.message || '(no message)'}
                      </p>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-gray-400">{formatWhen(e.created_at)}</span>
                        <button
                          onClick={() => handleDismiss(e.error_id)}
                          disabled={isBusy || !e.error_id}
                          className="p-0.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-40"
                          data-testid={`frontend-error-dismiss-${idx}`}
                          title="Dismiss this error"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    {e.url && (
                      <a
                        href={e.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-terracotta hover:underline break-all"
                      >
                        <ExternalLink className="h-3 w-3 shrink-0" />
                        {e.url.replace(window.location.origin, '')}
                      </a>
                    )}
                    {e.error_id && (
                      <p className="text-[10px] text-gray-400 mt-1 font-mono">
                        ref: {e.error_id}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FrontendErrorBadge;
